/**
 * PatternContentTroubleshooting
 * -----------------------------
 * WHAT: Renders the "Troubleshooting" section of a Pattern — a sortable
 *       table of { issue, solution } pairs. Used by ContentDataForm when a
 *       section's UIComponent is `PatternContentTroubleshooting`.
 *
 * WHY it's different from other sections:
 *   - Each row has TWO rich-text columns (issue + solution).
 *   - Rows are reorderable via drag-and-drop (dnd-kit SortableList).
 *   - Inline commenting is supported INSIDE each row's editor
 *     (TiptapEditorCommentView) — comments are keyed on
 *     path = ['troubleshooting', 'issue'|'solution', troubleshooting.id].
 *   - Uses CloudScape + a raw <awsui-table> web-component because Polaris
 *     Table doesn't support row reordering — see the comment inline.
 *
 * STYLE NOTE: this is a function component (uses hooks) while
 * ContentDataSection/Epics are class components. Good target shape for the rewrite.
 */
import { ExpandableSection, Box, Button, SpaceBetween, Modal } from '@amzn/awsui-components-core';
import {
  ContentAccessUser,
  ContentHistory,
  ContentWorkflow,
  Troubleshooting,
  ContentNode,
  ContentType,
  WorkflowState,
  UserCommentState,
} from '@amzn/awscat-pattern-library-service-model';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import constants from '../../../common/constants';
import { ContentSectionKey } from '../../../model/ContentSchemaSection';
import ContentSectionHeader from '../../content/ContentSectionHeader';
import TroubleshootingEditor from './TroubleshootingEditor';
import { reorderById } from '../../../common/Arrays';
import { MoveVerticallyHandle } from '../../common/MoveVerticallyHandle';
import { SortableList, SortableRenderArgs } from '../../v2-common/SortableList';
import {
  // DEAD IMPORT: TiptapEditorHTMLView is imported but never used here.
  TiptapEditorHTMLView,
  TiptapEditorCommentView,
  TiptapEditorView,
} from '@amzn/awscat-editor';
import TechwriterMetadataEditor from './TechwriterMetadataEditor';
import TechwriterMetadata from '../../../model/TechwriterMetadata';
import { ContentViewerContextData, withContentViewerContext } from '../ContentViewerContext';
import ErrorBoundary from '../../../editor/ErrorBoundary';
import { fetchImageInBase64Format } from '../../../editor/utils';
import { IMplServiceClient } from '../../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../../api/MplServiceClientFactory';
import AppContextProps from '../../../contexts/AppContextProps';
import { withAppContext } from '../../../contexts/AppContext';

// Ambient declaration: tells TypeScript that <awsui-table> is a valid JSX tag.
// Needed because we drop down to the raw web-component instead of using
// CloudScape's <Table> (which doesn't support row reorder).
declare module 'react' {
  namespace JSX {
    interface IntrinsicElements {
      'awsui-table': any;
    }
  }
}

// Stable across renders so SortableList's memoized id list stays valid.
const keyOfTroubleshooting = (troubleshooting: Troubleshooting) => troubleshooting.id;

export interface PatternContentTroubleshootingProps extends AppContextProps {
  troubleshootings: Troubleshooting[];
  readonly: boolean;
  onContentChanged?: (
    sectionId: string,
    key: ContentSectionKey,
    newContent: any,
    validationError?: any,
  ) => void;
  expanded?: boolean;
  contentId: string;
  workflowId: number;
  showSectionalComments?: boolean;
  workflow?: ContentWorkflow;
  workflowHistory?: ContentHistory[];
  coAuthors?: ContentAccessUser[];
  content: ContentNode;
  showTechwriterMetadata: boolean;
  sectionId: string;
  contentViewerContext: ContentViewerContextData; // For inline commenting.
}

const PatternContentTroubleshooting = (props: PatternContentTroubleshootingProps) => {
  // Controlled ExpandableSection state (sync'd from props.expanded in the effect below).
  const [sectionExpanded, setSectionExpanded] = useState<boolean>(true);
  // Visibility of the "Add new troubleshooting" inline editor.
  const [tsEditorVisible, setTsEditorVisible] = useState<boolean>(false);
  const [techwriterMetadata, setTechwriterMetadata] = useState<TechwriterMetadata>({
    abstract: props.content.abstract,
    href: props.content.href,
  });
  // Visibility of the per-row "Edit troubleshooting" modal.
  const [editingModalVisible, setEditingModalVisible] = useState<boolean>(false);
  // The row currently being edited in the modal.
  //
  // BUG: `useState(null)` without a generic gives it type `null`, so TS has
  // no idea this can later hold a Troubleshooting. The `any` escape hatch
  // works but loses type safety.
  const [currentTs, setCurrentTs] = useState(null);
  // Local copy of the list (initialized from props; re-synced via effect).
  const [troubleshootings, setTroubleshootings] = useState<Troubleshooting[]>(
    props.troubleshootings,
  );
  const {
    content,
    readonly,
    showTechwriterMetadata,
    sectionId,
    onContentChanged,
    workflow,
    appContext,
  } = props;
  const { entities } = appContext ?? {};
  // Cache a service client across renders. The factory creates a new client
  // each call, so useMemo avoids spinning up a new one every render.
  const mplServiceClient: IMplServiceClient = useMemo(() => MplServiceClientFactory.create(), []);

  // Mirror props.expanded -> state when parent toggles "Expand all" etc.
  useEffect(() => {
    // @ts-expect-error TS2345 - strict migration
    setSectionExpanded(props.expanded);
  }, [props.expanded]);

  // Re-sync local list when the parent pushes a new array.
  // The Array.isArray branch normalizes legacy single-object data to [item].
  useEffect(() => {
    setTroubleshootings(
      Array.isArray(props.troubleshootings) ? props.troubleshootings : [props.troubleshootings],
    );
  }, [props.troubleshootings]);

  // --- CRUD handlers -----------------------------------------------------
  // All of them bubble up via props.onContentChanged with sectionId
  // hard-coded to 'troubleshooting' (only one such section per pattern).

  /**
   * Append a new troubleshooting row.
   * BUG: uses `...troubleshootings` from the local state closure rather than
   * the latest props. If the parent updated the list between renders, we'll
   * merge against a stale snapshot and lose the parent's update.
   * Safer: pass an updater that reads from props.troubleshootings.
   */
  const addTroubleshooting = (newTroubleshooting: Troubleshooting) => {
    const updatedTroubleshootings = [...troubleshootings, newTroubleshooting];
    props.onContentChanged?.(
      'troubleshooting',
      ContentSectionKey.ContentData,
      updatedTroubleshootings,
    );
    setTsEditorVisible(false);
  };

  /**
   * Replace an existing row by id.
   * DEAD CODE: the `props.onContentChanged` nullish-call inside the if-block
   * is redundant because the outer `if` already requires it to be truthy.
   * Could simplify to a direct call.
   */
  const updateTroubleshooting = (troubleshooting: Troubleshooting) => {
    const updatedTroubleshootings = [...troubleshootings];
    const index = updatedTroubleshootings.findIndex((i) => i.id === troubleshooting.id);
    if (index >= 0 && props.onContentChanged) {
      updatedTroubleshootings.splice(index, 1, troubleshooting);
      setEditingModalVisible(false);
      props.onContentChanged?.(
        'troubleshooting',
        ContentSectionKey.ContentData,
        updatedTroubleshootings,
      );
    }
  };

  // Close the inline add-new editor without saving.
  const onCancel = () => {
    setTsEditorVisible(false);
  };

  // Open the modal pre-populated with a row for editing.
  const editEntry = (troubleshooting: Troubleshooting) => {
    if (troubleshooting) {
      // @ts-expect-error TS2345 - strict migration
      setCurrentTs(troubleshooting);
      setEditingModalVisible(true);
    }
  };

  /**
   * Delete a row after a native confirm() prompt.
   * BUG: uses the global `confirm()` which is synchronous and blocks the UI.
   * Prefer a CloudScape confirmation modal for a consistent look. Also, the
   * nullish-call `props.onContentChanged?.(...)` inside the if-block is again
   * redundant (already guarded by the outer if).
   */
  const deleteEntry = (troubleshooting: Troubleshooting) => {
    if (confirm(constants.TASK_REMOVE_CONFIRM_TEXT) && troubleshooting) {
      const updatedTroubleshootings = [...troubleshootings];
      const index = updatedTroubleshootings.findIndex((i) => i.id === troubleshooting.id);
      if (index >= 0 && props.onContentChanged) {
        updatedTroubleshootings.splice(index, 1);
        props.onContentChanged?.(
          'troubleshooting',
          ContentSectionKey.ContentData,
          updatedTroubleshootings,
        );
      }
    }
  };

  const addNewButtonClicked = () => {
    setTsEditorVisible(true);
  };

  // @ts-expect-error TS7006 - strict migration
  const expandSection = (e) => {
    e.preventDefault();
    setSectionExpanded(e.detail.expanded);
  };

  /**
   * Handler for drag-reorder. Reorders `props.troubleshootings` (not the local
   * `troubleshootings` state) so we reorder against the canonical list. Good —
   * avoids the stale-closure bug that addTroubleshooting has.
   */
  const onReorder = (orderedIds: readonly string[]) => {
    props.onContentChanged?.(
      'troubleshooting',
      ContentSectionKey.ContentData,
      reorderById(props.troubleshootings, orderedIds),
    );
  };

  // Abstract/href sync — same shape as in the other two section components.
  const updateTechwriterMetadata = (
    sectionKey: ContentSectionKey,
    value: any,
    validationError?: string,
  ) => {
    setTechwriterMetadata({
      ...techwriterMetadata,
      [sectionKey]: value,
    });
    if (onContentChanged) {
      onContentChanged(sectionId, sectionKey, value, validationError);
    }
  };

  // --- Presentation pieces ----------------------------------------------

  // Section header (title only; this section never allows rename/sort at
  // the section level because it's a built-in pattern section).
  const headerComponent = (
    <ContentSectionHeader
      sectionId={'troubleshooting'}
      title={constants.TROUBLESHOOTING_LABEL}
      isEditable={false}
      isSortable={false}
      readonly={props.readonly}
    />
  );

  const isGuideOrStrategy = [ContentType.Guide, ContentType.Strategy].includes(
    // @ts-expect-error TS2345 - strict migration
    workflow?.draftContentMetadata?.contentType,
  );
  const techWriterMetadataEditor = (
    <SpaceBetween size="xxxs">
      <TechwriterMetadataEditor
        readonly={readonly}
        visible={showTechwriterMetadata}
        metadata={techwriterMetadata}
        onInputUpdated={updateTechwriterMetadata}
        nodeType={content.nodeType}
        disabled={!isGuideOrStrategy}
      />
    </SpaceBetween>
  );

  // Edit + delete + drag handle buttons for a single row. Only rendered
  // in edit mode (wrapped by the `!props.readonly` guard in renderRow below).
  const editCell = (item: Troubleshooting) => (
    <Box float="right">
      <SpaceBetween direction="horizontal" size="m">
        <div>
          <Button
            onClick={() => editEntry(item)}
            iconUrl={'/images/icon-edit.svg'}
            iconAlt="Edit icon"
            variant="inline-icon"
            ariaLabel="Edit troubleshooting"
          />
          <Button
            onClick={() => deleteEntry(item)}
            iconUrl={'/images/icon-delete.svg'}
            iconAlt="Delete icon"
            variant="inline-icon"
            ariaLabel="Delete troubleshooting"
          />
        </div>
        <div>
          <MoveVerticallyHandle />
        </div>
      </SpaceBetween>
    </Box>
  );

  /**
   * Factory that returns a "create inline comment on this troubleshooting
   * row/field" callback. The comment path encodes:
   *   ['troubleshooting', 'issue'|'solution', <ts.id>]
   * so the server can filter comments per column per row.
   *
   * WHY a factory: the callback needs access to both the row id and the
   * column name at capture time; capturing from closure is simpler than
   * threading those args through TiptapEditorCommentView.
   */
  const createInlineCommentFunction = (troubleshooting: any, type: string) => {
    const path = ['troubleshooting', type, troubleshooting.id];

    const createInlineComment = async (
      startOffset: number,
      endOffset: number,
      textData: string,
      commentData: string,
    ) => {
      // @ts-expect-error TS2722 - strict migration
      await props?.contentViewerContext?.createInlineComment(
        path,
        startOffset,
        endOffset,
        textData,
        commentData,
      );
    };
    return createInlineComment;
  };

  // Image fetcher passed down to the editor. Images in Tiptap content are
  // stored as URLs the service needs to sign; this helper fetches and
  // base64-encodes them so they render inline without further round trips.
  const fetchImage = async (srcUrl: string) => {
    return await fetchImageInBase64Format(srcUrl, mplServiceClient);
  };

  /**
   * renderRow = a single <tr> in the troubleshooting table, rendered by
   * SortableList's `item` render function.
   *
   * WHY nested inside the component body:
   *   renderRow needs access to props/state/context (comments, entities,
   *   publicationState, etc.). Defining it here avoids prop drilling. It is a
   *   plain render function (not a component type), so rows keep their
   *   identity across re-renders instead of remounting.
   *
   * BUG: `TiptapEditorView` is used when commenting is disabled and takes NO
   * `comments` prop. But in the "commentingMode" branch we use
   * TiptapEditorCommentView with the issue/solution comments. The switch is
   * controlled by `disableCommentingMode`, which is true when the workflow is
   * PUBLISHED/DISTRIBUTED or commenting mode is off. Works, but the logic is
   * inverted-sounding ("disable" is the normal reading case).
   */
  const renderRow = (
    troubleshooting: Troubleshooting,
    { setNodeRef, style }: SortableRenderArgs,
  ) => {
    // Resolve the active (non-resolved) comments for the "issue" column of this row.
    const issueComments = props.contentViewerContext?.inlineComments
      ?.filter(
        (comment) =>
          comment.path[0] === 'troubleshooting' &&
          comment.path?.[1] === 'issue' &&
          comment.path?.[2] === troubleshooting.id,
      )
      ?.filter((comment) => comment.state !== UserCommentState.RESOLVED);
    const { publicationState, isCommentingModeEnabled } = props?.contentViewerContext ?? {};
    // Published/distributed content is frozen, so comments can't be added;
    // also honour the user-toggled "commenting mode".
    const disableCommentingMode =
      // @ts-expect-error TS2345 - strict migration
      [WorkflowState.PUBLISHED, WorkflowState.DISTRIBUTED].includes(publicationState) ||
      !isCommentingModeEnabled;
    const issueEditor = disableCommentingMode ? (
      <TiptapEditorView
        // @ts-expect-error TS2322 - strict migration
        model={troubleshooting?.issue}
        // @ts-expect-error TS2322 - strict migration
        entities={entities}
        fetchImage={fetchImage}
      />
    ) : (
      <TiptapEditorCommentView
        // @ts-expect-error TS2322 - strict migration
        model={troubleshooting?.issue}
        comments={issueComments}
        addInlineComment={createInlineCommentFunction(troubleshooting, 'issue')}
        // @ts-expect-error TS2322 - strict migration
        entities={entities}
        fetchImage={fetchImage}
      />
    );
    // Wrap in ErrorBoundary so a broken piece of content (bad JSON / entity)
    // doesn't blow up the whole section; falls back to rendering the raw editor.
    //
    // BUG: the fallback IS the same component that threw. If the initial render
    // crashed, showing the same component again will crash again. Typical
    // ErrorBoundary fallbacks are a lightweight static placeholder, not the
    // failing component.
    const issue = <ErrorBoundary fallback={issueEditor}>{issueEditor}</ErrorBoundary>;
    const solutionComments = props.contentViewerContext?.inlineComments
      ?.filter(
        (comment) =>
          comment.path[0] === 'troubleshooting' &&
          comment.path?.[1] === 'solution' &&
          comment.path?.[2] === troubleshooting.id,
      )
      ?.filter((comment) => comment.state !== UserCommentState.RESOLVED);
    const solutionEditor = disableCommentingMode ? (
      <TiptapEditorView
        // @ts-expect-error TS2322 - strict migration
        model={troubleshooting?.solution}
        // @ts-expect-error TS2322 - strict migration
        entities={entities}
        fetchImage={fetchImage}
      />
    ) : (
      <TiptapEditorCommentView
        // @ts-expect-error TS2322 - strict migration
        model={troubleshooting?.solution}
        comments={solutionComments}
        addInlineComment={createInlineCommentFunction(troubleshooting, 'solution')}
        // @ts-expect-error TS2322 - strict migration
        entities={entities}
        fetchImage={fetchImage}
      />
    );
    const solution = <ErrorBoundary fallback={solutionEditor}>{solutionEditor}</ErrorBoundary>;
    return (
      <tr ref={setNodeRef} style={style} className="awsui-table-row task-list-table-row ">
        <td>
          <span>
            {/* Nested <span>s match the awsui-table web-component's expected
                DOM so its CSS selectors find and style them. */}
            <span>{issue}</span>
          </span>
        </td>
        <td>
          <span>
            <span>{solution}</span>
          </span>
        </td>
        {/* Edit/delete/drag cell — only shown in edit mode. */}
        {!props.readonly ? (
          <td className="empty-column-width">
            <span>
              <span>{editCell(troubleshooting)}</span>
            </span>
          </td>
        ) : null}
      </tr>
    );
  };

  /**
   * renderTable = SortableList container wrapping the sortable rows in <tbody>.
   * Raw <awsui-table> web-component is used here instead of CloudScape <Table>
   * because Polaris/CloudScape's Table component does not support drag-to-reorder.
   */
  const renderTable = (rows: ReactNode) => {
    return (
      // Using awsui-table instead because Polaris table doesn't support re-ordering rows
      <awsui-table initialized="true">
        <div className="task-list-table awsui-table-inner awsui-table-variant-default">
          <div className="awsui-table-container">
            <table>
              <thead>
                <tr>
                  <th className="troubleshooting-issue-col">
                    <span className="awsui-table-header-content">
                      <span>
                        <span>Issue</span>
                      </span>
                    </span>
                  </th>
                  <th>
                    <span className="awsui-table-header-content">
                      <span>
                        <span>Solution</span>
                      </span>
                    </span>
                  </th>
                  {!props.readonly ? (
                    <th>
                      <span className="awsui-table-header-content" />
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>{rows}</tbody>
            </table>
          </div>
        </div>
      </awsui-table>
    );
  };

  // "+ Add issue" button shown below the table in edit mode.
  const addNewTsButton = (
    <Box margin={{ top: 'l' }}>
      <Button iconName="add-plus" onClick={addNewButtonClicked}>
        {constants.TROUBLESHOOTING_ADD_ISSUE_BUTTON_TEXT}
      </Button>
    </Box>
  );

  // Inline editor shown in place of the add button while the user is
  // composing a new row. Two-column (issue / solution) editor.
  const tsEditor = (
    <TroubleshootingEditor
      contentId={props.contentId}
      sectionId={'troubleshooting'}
      onAddConfirmed={addTroubleshooting}
      onCancel={onCancel}
    />
  );

  // Modal for editing an existing row — same TroubleshootingEditor but
  // seeded with the row's current values and laid out vertically.
  //
  // DEAD CODE: modal's `onDismiss` closes via click-outside / ESC / close
  // button indiscriminately. Consistent with the page-title modal in
  // ContentDataForm but different from the section-title modal in
  // ContentDataSection (which uses handleDismissModal). Worth aligning.
  const editingModal = (
    <Modal
      onDismiss={() => setEditingModalVisible(false)}
      visible={editingModalVisible}
      closeAriaLabel="Close modal"
      size="medium"
      footer={
        <TroubleshootingEditor
          contentId={props.contentId}
          sectionId={'troubleshooting'}
          // @ts-expect-error TS2322 - strict migration
          troubleshooting={currentTs}
          onAddConfirmed={updateTroubleshooting}
          onCancel={() => setEditingModalVisible(false)}
          direction="vertical"
        />
      }
      header="Edit troubleshooting"
    />
  );

  // Empty state: hidden while the add editor is open (to avoid showing both
  // a placeholder and the editor at once).
  const emptyMessage = tsEditorVisible ? null : <div>{constants.TROUBLESHOOTING_PLACEHOLDER}</div>;

  const troubleshootingTable = (
    <SortableList
      items={troubleshootings}
      keyExtractor={keyOfTroubleshooting}
      onReorder={onReorder}
      isDisabled={props.readonly}
      container={renderTable}
      item={renderRow}
    />
  );

  // Readonly: table only, no expansion, no add button. Shows placeholder when empty.
  const readonlyDisplay = (
    <div>
      {headerComponent}
      {techWriterMetadataEditor}
      {troubleshootings.length > 0 ? troubleshootingTable : emptyMessage}
    </div>
  );

  // Edit: wrapped in ExpandableSection; also shows add button / add editor / edit modal.
  const editDisplay = (
    <div>
      <ExpandableSection
        header={headerComponent}
        variant="container"
        onChange={expandSection}
        expanded={sectionExpanded}
      >
        {techWriterMetadataEditor}
        {troubleshootings.length > 0 ? troubleshootingTable : emptyMessage}
        {tsEditorVisible ? tsEditor : addNewTsButton}
        {editingModal}
      </ExpandableSection>
    </div>
  );

  return props.readonly ? readonlyDisplay : editDisplay;
};

// Inject ContentViewerContext (for inline comments) and AppContext (for
// entities). Order is: innermost HOC runs first, so `appContext` is set by
// withAppContext and then `contentViewerContext` is set by the outer
// withContentViewerContext.
export default withContentViewerContext(withAppContext(PatternContentTroubleshooting));
